<template>
	<form
		@submit.prevent="
			() => {
				if (showCheckbox && !state.isChecked) return
				emit('submit')
			}
		"
		class="flex flex-col fade-in"
		autocomplete="off"
	>
		<div v-if="showBack || title" class="flex items-center">
			<Icon
				v-if="showBack"
				name="mdi:arrow-left"
				class="w-8 h-8 duration-300 transform rounded-md cursor-pointer rtl:rotate-180 text-natural-dark hover:text-primary me-2"
				@click="emit('back')"
			/>
			<span v-if="title" class="text-3xl font-bold">
				<span class="text-natural-dark">{{ title }}</span>
				<span class="text-primary">.</span>
			</span>
		</div>
		<span v-if="subtitle || subtitleLink" class="mt-2 text-sm">
			<span v-if="subtitle" class="text-natural-semidark me-2">
				{{ subtitle }}
			</span>
			<span
				v-if="subtitleLink"
				class="font-medium cursor-pointer text-primary"
				@click="emit('click-subtitle-link')"
			>
				{{ subtitleLink }}
			</span>
		</span>
		<span class="h-8" v-if="title || showBack || subtitle || subtitleLink" />
		<div
			v-if="showEditEmail"
			class="flex items-center self-center mb-8 text-xs bg-opacity-25 rounded-full cursor-pointer bg-primary text-natural-semidark pe-4"
			@click="emit('edit-email')"
		>
			<Icon name="mdi:email" class="w-5 h-5 mx-3 my-2 duration-300 rounded-full" />
			<span class="py-2" dir="ltr">{{ data.email }}</span>
		</div>
		<div
			v-if="showEditMobile"
			class="flex items-center self-center mb-8 text-xs bg-opacity-25 rounded-full cursor-pointer bg-primary text-natural-semidark pe-4"
			@click="emit('edit-mobile')"
		>
			<Icon name="mdi:phone" class="w-5 h-5 mx-3 my-2 duration-300 rounded-full" />
			<span class="py-2" dir="ltr">{{ data.mobile }}</span>
		</div>
		<div v-if="showModeSelector" class="flex self-center w-full max-w-lg px-4 mb-6 overflow-hidden rounded-t-xl">
			<div
				v-for="m in modes"
				:key="m.id"
				:class="[
					'px-4 py-3 text-center truncate text-sm flex-grow flex-shrink-0 cursor-pointer border-b-4 duration-300',
					mode == m.id
						? 'text-primary bg-primary bg-opacity-10 border-primary font-bold'
						: 'text-slate-800 bg-slate-50 hover:bg-slate-100 border-slate-200 font-normal'
				]"
				@click="() => emit('update:mode', m.id)"
			>
				{{ m.title }}
			</div>
		</div>
		<div class="grid grid-cols-1 gap-6">
			<BaseTextInput
				v-if="showEmail"
				:placeholder="$t('email')"
				:value="data.email"
				name="email"
				@change="(v) => changeData({ email: v })"
				end-icon="mdi:email"
				:disabled="disabled"
				theme-type="outlined"
			/>
			<BaseMobileInput
				v-if="showMobile"
				:model-value="data.mobile"
				@update:model-value="(v) => changeData({ mobile: v })"
			/>
			<BaseTextInput
				v-if="showFirstname"
				:placeholder="$t('firstname')"
				:value="data.firstname"
				name="firstname"
				@change="(v) => changeData({ firstname: v })"
				end-icon="mdi:account"
				:disabled="disabled"
				theme-type="outlined"
			/>
			<BaseTextInput
				v-if="showLastname"
				:placeholder="$t('lastname')"
				name="lastname"
				:value="data.lastname"
				@change="(v) => changeData({ lastname: v })"
				end-icon="mdi:account"
				:disabled="disabled"
				theme-type="outlined"
			/>
			<span v-if="showAddress" class="cursor-pointer" @click="state.isSelectLocationDialogVisible = true">
				<BaseTextInput
					:placeholder="$t('address')"
					:value="data.address"
					end-icon="mdi:map-marker"
					:disabled="true"
					class="pointer-events-none"
					theme-type="outlined"
				/>
			</span>
			<BaseTextInput
				v-if="showPassword"
				type="password"
				:placeholder="$t('password')"
				:value="data.password"
				end-icon="mdi:lock"
				@change="(v) => changeData({ password: v })"
				:autocomplete="showRePassword ? 'new-password' : ''"
				:disabled="disabled"
				theme-type="outlined"
			/>
			<input v-if="showEmail || showMobile" type="password" hidden :value="data.password || undefined" />
			<BaseTextInput
				v-if="showRePassword"
				type="password"
				:placeholder="$t('repeat_password')"
				:value="data.re_password"
				end-icon="mdi:lock"
				@change="(v) => changeData({ re_password: v })"
				autocomplete="new-password"
				:disabled="disabled"
				theme-type="outlined"
			/>
			<BaseTextInput
				v-if="showCode"
				type="text"
				:placeholder="$t('confirmation_code')"
				:value="data.code"
				end-icon="material-symbols:verified-user"
				@change="(v) => changeData({ code: v })"
				:disabled="disabled"
				theme-type="outlined"
			/>
			<!-- <MaterialLabel v-if="showCaptcha" :label="$t('captcha')" :value="data.captcha">
				<div class="flex">
					<input
						type="text"
						:value="data.captcha"
						@input="onChangeUpperCase"
						@change="(e) => changeData({ captcha: e.target.value })"
						class="font-serif materiallabel-input"
						:disabled="disabled"
					/>
					<div
						:style="{ height: '58px', width: '110px' }"
						class="relative flex-shrink-0 w-auto mx-1 my-1"
						@click="getCaptcha"
					>
						<img
							:src="captcha"
							alt=""
							class="absolute top-0 bottom-0 left-0 right-0 object-fill w-full h-full rounded-lg"
						/>
						<div
							v-if="captchaLoading"
							class="absolute top-0 bottom-0 left-0 right-0 w-4 h-4 m-auto rounded-full bg-primary animate-ping"
						/>
						<div
							class="absolute top-0 bottom-0 left-0 right-0 w-full h-full border-2 rounded-lg border-natural-semidark materiallabel-focus"
						/>
					</div>
				</div>
			</MaterialLabel> -->
		</div>
		<div class="flex-grow"></div>
		<span
			v-if="showCheckbox"
			class="flex items-center self-start -mb-2 mt-8"
			@click="state.isChecked = !state.isChecked"
		>
			<BaseCheckBox :model-value="state.isChecked" class="me-2.5 w-5 h-5 flex-shrink-0" />
			<span class="text-xs text-slate-700">
				<I18nT keypath="auth_checkbox_text">
					<template #app_name>
						{{ $t("app_name") }}
					</template>
					<template #terms>
						<NuxtLink :to="$routeUrl.TermsOfServiceUrl()" class="text-primary font-semibold">
							{{ $t("terms_of_service") }}
						</NuxtLink>
					</template>
					<template #privacy>
						<NuxtLink :to="$routeUrl.PrivacyPolicyUrl()" class="text-primary font-semibold">
							{{ $t("privacy_policy") }}
						</NuxtLink>
					</template>
				</I18nT>
			</span>
		</span>
		<button
			v-if="submitTitle"
			type="submit"
			:class="[
				'px-6 py-4 mt-8 mb-2 font-bold text-center text-white rounded-lg cursor-pointer',
				showCheckbox && !state.isChecked ? 'ripple-bg-slate-400 pointer-events-none' : 'ripple-bg-primary'
			]"
		>
			{{ submitTitle }}
		</button>
		<span
			v-if="bottomLink"
			class="self-center mt-2 text-sm font-bold duration-300 cursor-pointer text-natural-semidark hover:text-primary"
			@click="emit('click-bottom-link')"
		>
			{{ bottomLink }}
		</span>
		<BaseSelectLocationDialog
			:visible="state.isSelectLocationDialogVisible"
			:center="data?.longitude && data?.latitude ? [data.longitude, data.latitude] : state.center"
			:zoom="15"
			:loading="state.modalLoading"
			@close="state.isSelectLocationDialogVisible = false"
			@select="selectLocation"
		/>
	</form>
</template>

<script setup lang="ts">
import { UserAuthMode } from "~/stores/user"

const app = useNuxtApp()
const userStore = useUserStore()
const addressStore = useAddressStore()

type AuthFormData = {
	email?: string
	mobile?: string
	firstname?: string
	lastname?: string
	longitude?: number
	latitude?: number
	address?: string
	captcha: string
	captcha_key: string
	password: string
	re_password: string
	code: string
}

const emit = defineEmits<{
	change: [AuthFormData]
	submit: []
	back: []
	"update:loading": [boolean]
	"update:mode": [UserAuthMode["id"]]
	"click-subtitle-link": []
	"edit-email": []
	"edit-mobile": []
	"click-bottom-link": []
}>()

const props = defineProps<{
	title?: string
	subtitle?: string
	subtitleLink?: string
	bottomLink?: string
	submitTitle?: string
	showEmail?: boolean
	showMobile?: boolean
	showCode?: boolean
	showBack?: boolean
	showCaptcha?: boolean
	showPassword?: boolean
	showRePassword?: boolean
	showEditEmail?: boolean
	showEditMobile?: boolean
	showFirstname?: boolean
	showLastname?: boolean
	showAddress?: boolean
	showCheckbox?: boolean
	showModeSelector?: boolean
	data: AuthFormData
	wrongCount?: number
	disabled?: boolean
	mode?: UserAuthMode["id"]
}>()

const state = reactive({
	center: [-104.006, 40.7128],
	isSelectLocationDialogVisible: false,
	modalLoading: false,
	isChecked: false
})

const modes = computed(() => userStore.authModes)

if (props.showAddress) {
	const { coords } = useGeolocation()
	watch(
		() => coords.value,
		(n, o) => {
			if (n?.longitude == Infinity || n?.latitude == Infinity) {
				return
			}
			if (o?.longitude != Infinity && o?.latitude != Infinity) {
				return
			}
			state.center = [coords.value.longitude, coords.value.latitude]
		},
		{ immediate: true }
	)
}

watch(
	() => props.wrongCount,
	() => {
		if (props.showCaptcha) {
			getCaptcha()
		}
	}
)

const changeData = (d: AuthFormData | any) => {
	emit("change", {
		...props.data,
		...d
	})
}

const onChangeUpperCase = (e: any) => {
	const v = e.target.value as string
	e.target.value = v?.toUpperCase() || ""
}

const selectLocation = async (location: number[] | null) => {
	if (!location) {
		state.isSelectLocationDialogVisible = false
		return
	}
	const oldLocation = [props.data?.longitude, props.data?.latitude]
	emit("change", {
		...props.data!,
		longitude: location?.[0] || props.data?.longitude,
		latitude: location?.[1] || props.data?.latitude
	})
	state.modalLoading = true
	try {
		const r = await addressStore.reverseGeocode({
			longitude: location[0],
			latitude: location[1]
		})
		if (!r.address) {
			throw { message: app.$i18n.t("address_not_found") }
		}
		emit("change", {
			...props.data!,
			longitude: location?.[0] || props.data?.longitude,
			latitude: location?.[1] || props.data?.latitude,
			address: r.address || ""
		})
		state.isSelectLocationDialogVisible = false
	} catch (e) {
		emit("change", {
			...props.data!,
			longitude: oldLocation[0],
			latitude: oldLocation[1]
		})
		app.$toastErrors(e)
	}
	state.modalLoading = false
}

const {
	data: captcha,
	pending: captchaLoading,
	error,
	refresh: getCaptcha
} = await useAsyncData(() => userStore.getCaptcha())

onMounted(() => {
	if (props.showCaptcha) {
		getCaptcha()
	}
})
</script>
